import { useCallback, useEffect, useRef, useState } from "react";
import { View } from "@-/com.npmjs.react-native";
import {
  BottomSheet,
  type BottomSheetHandle,
} from "@-/dev.improve.haniverse.components.ui.bottom-sheet";
import { Button } from "@-/dev.improve.haniverse.components.ui.button";
import { Icon } from "@-/dev.improve.haniverse.components.ui.icon";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { getDeviceSetting, setDeviceSetting } from "@-/dev.improve.haniverse.lib.device-settings";
import {
  registerSentenceVoiceChooser,
  type SentenceVoiceChoiceRequest,
  stopSentenceSpeech,
} from "@-/dev.improve.haniverse.lib.sentence.speech";
import {
  KOKORO_SENTENCE_VOICE_MODEL,
  NEURAL_SENTENCE_VOICES,
  type SentenceVoiceId,
} from "@-/dev.improve.haniverse.lib.sentence.speech.voice-catalog";
import { formatDownloadSize } from "#format-download-size";
import { SentenceVoiceOptionList } from "#sentence-voice-option-list";

/** The first natural voice costs the shared model plus its own style table (as its row shows). */
function firstVoiceDownloadBytes(): number {
  const recommended = NEURAL_SENTENCE_VOICES.find((voice) => voice.recommended);
  return KOKORO_SENTENCE_VOICE_MODEL.bytes + (recommended?.bytes ?? 0);
}

/**
 * Asks which voice reads sentences, the first time the learner plays one. Mounted once in the app
 * shell; the sentence player opens it through `registerSentenceVoiceChooser` and waits for the
 * answer, so the sentence that was tapped plays as soon as the chosen voice can speak.
 *
 * Choosing a natural voice downloads it here, with progress, and the sheet closes when it is
 * ready. Dismissing the sheet leaves the choice open (the next sentence asks again), but a
 * download already started still completes and becomes the choice.
 */
export function SentenceVoiceChooserSheet() {
  const sheetRef = useRef<BottomSheetHandle>(null);
  const requestRef = useRef<SentenceVoiceChoiceRequest | null>(null);
  const [isRequested, setIsRequested] = useState(false);

  useEffect(
    () =>
      registerSentenceVoiceChooser((request) => {
        // A second sentence tapped while the sheet is open replaces the first one's wait.
        requestRef.current?.resolve(null);
        requestRef.current = request;
        setIsRequested(true);
      }),
    [],
  );

  useEffect(() => {
    if (isRequested) {
      sheetRef.current?.open();
    }
  }, [isRequested]);

  const choose = useCallback((voiceId: SentenceVoiceId) => {
    // A download finishing after a dismissal still settles the open question.
    if (getDeviceSetting("sentenceVoice") === null || requestRef.current !== null) {
      setDeviceSetting("sentenceVoice", voiceId);
    }
    // Resolving lets the waiting sentence play; it interrupts any sample still playing.
    requestRef.current?.resolve(voiceId);
    requestRef.current = null;
    sheetRef.current?.close();
  }, []);

  const reportProgress = useCallback(() => {
    requestRef.current?.reportProgress();
  }, []);

  return (
    <BottomSheet
      footer={
        <Button
          onPress={() => sheetRef.current?.close()}
          testID="sentence-voice-chooser-dismiss"
          variant="ghost"
        >
          <Text>Not now</Text>
        </Button>
      }
      onDismiss={() => {
        // After a choice the request is already settled and its sentence is playing; only a
        // dismissal without one ("Not now", a swipe) stops a sample and drops the waiting sentence.
        if (requestRef.current !== null) {
          stopSentenceSpeech();
          requestRef.current.resolve(null);
          requestRef.current = null;
        }
        setIsRequested(false);
      }}
      ref={sheetRef}
      scrollable
    >
      <View className="gap-4 pb-4" testID="sentence-voice-chooser-sheet">
        <View className="items-center gap-2 px-4">
          <Icon className="text-foreground" name="MdRecordVoiceOver" size={32} />
          <Text
            accessibilityRole="header"
            className="text-center font-sans text-[22px] font-semibold"
            colorVariant="default"
          >
            Choose a voice for sentences
          </Text>
          <Text className="text-center font-sans text-[15px]" colorVariant="muted">
            Natural voices sound like a real speaker and say every tone exactly as the app&apos;s
            pinyin shows it. They run on your phone after a one-time{" "}
            {formatDownloadSize(firstVoiceDownloadBytes())} download. Tap the speaker to hear a
            sample first.
          </Text>
        </View>
        <View className="overflow-hidden rounded-xs bg-surface">
          <SentenceVoiceOptionList
            onChoose={choose}
            onDownloadProgress={reportProgress}
            selectedVoiceId={null}
          />
        </View>
        <Text className="px-4 text-center text-xs" colorVariant="muted">
          You can change the voice any time in Settings → Sentence Voice.
        </Text>
      </View>
    </BottomSheet>
  );
}
